import React, {useContext} from "react";
import {Avatar, Box, Button, ButtonBase, Drawer, IconButton, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
    Building03Icon, Cancel01Icon, ConstructionIcon, Folder01Icon, Key01Icon, Logout01Icon, Settings02Icon,
    UserMultipleIcon
} from "@hugeicons/core-free-icons";
import {NavLink, useLocation} from "react-router-dom";
import {WebSettingsContext} from "../../../context/WebSettingsContext";
import {MODEL_LINKS} from "./modelLinks";

const rowSx = (active, danger) => ({
    width: "100%", justifyContent: "flex-start", gap: 1.5, height: 44, px: 1.5, borderRadius: "12px",
    fontSize: 14, fontWeight: active ? 700 : 500, textAlign: "left", textDecoration: "none",
    color: active ? "#4B55C9" : danger ? "#C73A3A" : "#23264D",
    bgcolor: active ? "rgba(132, 145, 247, 0.14)" : "transparent",
    transition: "background-color .15s ease",
    "&:hover": {bgcolor: active ? "rgba(132, 145, 247, 0.14)" : danger ? "#FDECEC" : "#FAFAFD"},
});

const Kicker = ({children}) => (
    <Typography sx={{
        px: 1.5, pt: 1.5, pb: 0.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7",
        textTransform: "uppercase",
    }}>
        {children}
    </Typography>
);

const NavRow = ({to, icon, label, onClick, end}) => {
    const {pathname} = useLocation();
    const active = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
    return (
        <ButtonBase component={NavLink} to={to} end={end} onClick={onClick} sx={rowSx(active)}>
            <Box sx={{display: "flex", flexShrink: 0}}><HugeiconsIcon icon={icon} size={20} strokeWidth={1.5}/></Box>
            {label}
        </ButtonBase>
    );
};

const ActionRow = ({icon, label, onClick, danger, iconColor}) => (
    <ButtonBase onClick={onClick} sx={rowSx(false, danger)}>
        <Box sx={{display: "flex", flexShrink: 0, color: iconColor}}><HugeiconsIcon icon={icon} size={20} strokeWidth={1.5}/></Box>
        {label}
    </ButtonBase>
);

const initialsOf = (user) => [user?.name || user?.firstName, user?.lastname || user?.lastName]
    .filter(Boolean).map(part => part[0]).join("").toUpperCase() || (user?.email?.[0] || "?").toUpperCase();

export const HeaderDrawer = ({open, handleClose, isAuth, isAdmin, isManager, currentUser, onLogout, openModal}) => {
    const modelLinks = isAdmin ? MODEL_LINKS : MODEL_LINKS.filter(link => !link.adminOnly);

    const {settings, onUnderMaintenance} = useContext(WebSettingsContext);

    const logoutHandle = async () => {
        try {
            await onLogout();
            handleClose();
        } catch (err) {
            console.error(err);
        }
    }

    const handleNav = () => handleClose();

    return (
        <Drawer
            anchor={"right"}
            open={open}
            onClose={handleClose}
            sx={{display: {xs: "inherit", md: "none"}}}
            PaperProps={{
                sx: {
                    width: 300, maxWidth: "85vw", bgcolor: "#fff", borderRadius: "20px 0 0 20px",
                    boxShadow: "0 12px 40px rgba(35, 38, 77, 0.16)",
                },
            }}
        >
            <Stack sx={{height: "100%", p: 2}}>
                <Stack direction={"row"} alignItems={"center"} gap={1.25} sx={{px: 0.5, pb: 1}}>
                    {isAuth && currentUser ? (
                        <>
                            <Avatar sx={{
                                width: 42, height: 42, fontSize: 14, fontWeight: 700, color: "#4B55C9",
                                bgcolor: "rgba(132, 145, 247, 0.16)",
                            }}>
                                {initialsOf(currentUser)}
                            </Avatar>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <Typography noWrap sx={{fontSize: 15, fontWeight: 700, color: "#23264D"}}>
                                    {currentUser?.name} {currentUser?.lastname}
                                </Typography>
                                {currentUser?.email && (
                                    <Typography noWrap sx={{fontSize: 12, color: "#8A8AA0"}}>{currentUser.email}</Typography>
                                )}
                            </Box>
                        </>
                    ) : <Box sx={{flex: 1}}/>}
                    <IconButton onClick={handleClose} aria-label={"Close menu"} sx={{
                        width: 38, height: 38, flexShrink: 0, color: "#23264D", bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"},
                    }}>
                        <HugeiconsIcon icon={Cancel01Icon} size={20}/>
                    </IconButton>
                </Stack>

                <Box sx={{flex: 1, minHeight: 0, overflowY: "auto"}}>
                    <Stack gap={0.5}>
                        {isAdmin && (
                            <>
                                <Kicker>Workspace</Kicker>
                                <NavRow to={"/projects"} icon={Folder01Icon} label={"Projects"} onClick={handleNav} end/>
                                <NavRow to={"/organizations"} icon={Building03Icon} label={"Organizations"} onClick={handleNav}/>
                                <NavRow to={"/users"} icon={UserMultipleIcon} label={"Users"} onClick={handleNav}/>
                            </>
                        )}

                        {(isAdmin || isManager) && (
                            <>
                                {!isAdmin && <Kicker>Workspace</Kicker>}
                                <NavRow to={"/backend-configuration"} icon={Settings02Icon} label={"Backend Configuration"}
                                        onClick={handleNav}/>
                                <Kicker>Models</Kicker>
                                {modelLinks.map(link => (
                                    <NavRow key={link.to} to={link.to} icon={link.icon} label={link.label} onClick={handleNav}/>
                                ))}
                            </>
                        )}

                        {isAuth && (
                            <>
                                <Kicker>Account</Kicker>
                                <NavRow to={"/api-keys"} icon={Key01Icon} label={"API Keys"} onClick={handleNav}/>
                            </>
                        )}

                        {isAdmin && (
                            <ActionRow
                                icon={ConstructionIcon}
                                label={"Under Maintenance"}
                                onClick={onUnderMaintenance}
                                danger={Boolean(settings?.underMaintenance)}
                            />
                        )}

                        {isAuth ? (
                            <ActionRow icon={Logout01Icon} label={"Logout"} onClick={logoutHandle} iconColor={"#8A8AA0"}/>
                        ) : (
                            <Box sx={{pt: 1}}>
                                <Button
                                    fullWidth
                                    onClick={() => {
                                        openModal();
                                        handleClose();
                                    }}
                                    sx={{
                                        height: 44, borderRadius: "12px", textTransform: "none", fontSize: 14, fontWeight: 600,
                                        color: "#374151", bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"},
                                    }}
                                >
                                    Sign in
                                </Button>
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Drawer>
    )
}
